import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Card,
  IconButton,
  alpha
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { uploadService } from '../services/uploadService';
import { useToast } from '../contexts/ToastContext';
import { getImageUrl } from '../utils/imageUtils';

interface ImageUploadProps {
  value?: string;
  onChange: (imageUrl: string | null) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  disabled = false,
  label = "Imagem da Campanha",
  helperText = "Formatos suportados: JPG, PNG, GIF, WEBP. Tamanho máximo: 5MB"
}) => {
  const theme = useTheme();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (file: File) => {
    if (disabled) return;

    // Validações do lado do cliente
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Formato de arquivo não suportado. Use JPG, PNG, GIF ou WEBP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      showError('Arquivo muito grande. Tamanho máximo: 5MB.');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Iniciando upload de imagem:', file.name, file.size, 'bytes');
      const response = await uploadService.uploadImage(file);
      console.log('✅ Upload bem-sucedido:', response.imageUrl);
      onChange(response.imageUrl);
      showSuccess('Imagem enviada com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao fazer upload:', error);
      showError('Erro ao enviar imagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [disabled, onChange, showSuccess, showError]);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleButtonClick = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    if (disabled) return;
    onChange(null);
  };

  // Removida função local getImageUrl - usando a do utils/imageUtils.ts

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      
      {value ? (
        // Preview da imagem
        <Card sx={{ position: 'relative', maxWidth: 400 }}>
          <Box sx={{ position: 'relative' }}>
            <img
              src={getImageUrl(value)}
              alt="Preview da campanha"
              style={{
                width: '100%',
                height: '200px',
                objectFit: 'cover',
                borderRadius: '8px',
                display: 'block'
              }}
              onError={(e) => {
                console.error('Erro ao carregar imagem:', value);
                console.error('URL completa tentada:', getImageUrl(value));
                // Limpar o valor para mostrar a área de upload novamente
                onChange(null);
              }}
              onLoad={() => {
                console.log('Imagem carregada com sucesso:', getImageUrl(value));
              }}
            />
            {!disabled && (
              <IconButton
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: alpha(theme.palette.error.main, 0.8),
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.error.main,
                  }
                }}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        </Card>
      ) : (
        // Área de upload
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: dragOver 
              ? alpha(theme.palette.primary.main, 0.05)
              : alpha(theme.palette.action.hover, 0.02),
            padding: 4,
            textAlign: 'center',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': !disabled ? {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            } : {}
          }}
        >
          {loading ? (
            <Box>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Enviando imagem...
              </Typography>
            </Box>
          ) : (
            <Box>
              <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {dragOver ? 'Solte a imagem aqui' : 'Clique no botão abaixo ou arraste uma imagem'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                disabled={disabled}
                onClick={handleButtonClick}
                sx={{ mt: 1 }}
              >
                Selecionar Imagem
              </Button>
            </Box>
          )}
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}; 